// --- React
import { useCallback, useRef, useState } from 'react';

/**
 * Custom hook to manage AG Grid API and row expansion state.
 *
 * @returns {Object} - An object containing the AG Grid API reference and functions to manage row expansion.
 */
export const useAgGridApi = () => {
    const AgGridApiRef = useRef();
    const [isMasterRowsExpanded, setIsExpanded] = useState(false);

    const getSelectedRows = useCallback(() => {
        // Check if the API reference is available before accessing it
        if (!AgGridApiRef.current) return;

        // Check if the API reference has the 'api' property
        if (!AgGridApiRef.current.api) return;

        // Check if the API reference has the 'getSelectedRows' method
        if (!AgGridApiRef.current.api.getSelectedRows) return;

        const { api } = AgGridApiRef.current;
        return api.getSelectedRows();
    }, [AgGridApiRef]);

    // Function to toggle the expansion state of all rows
    const _toggleRows = (toggleTo) => () => {
        try {
            // Check if the API reference is available before accessing it
            if (AgGridApiRef.current != null) {
                const { api } = AgGridApiRef.current;

                // Check if the API reference has the 'forEachNode' method
                if (!api.forEachNode) return;

                // Check if the API reference has the 'setRowNodeExpanded' method
                if (!api.setRowNodeExpanded) return;

                // Check if the API reference has the 'refreshCells' method
                if (!api.refreshCell) return;

                if (api) {
                    // Iterate through all nodes and set their expansion state
                    api.forEachNode((node) => {
                        node.setExpanded(toggleTo);
                        api.setRowNodeExpanded(node, toggleTo);
                    });

                    // Refresh the cells to reflect the changes
                    api.refreshCells({ force: true });
                }
            }
        } finally {
            // Update the expansion state
            setIsExpanded(toggleTo);
        }
    };

    // Function to toggle the expansion state of master rows
    const toggleMasterRows = useCallback(() => {
        if (isMasterRowsExpanded === true) {
            _toggleRows(false)();
        } else {
            _toggleRows(true)();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AgGridApiRef, isMasterRowsExpanded]);

    // Function to collapse all master rows
    const collapseMasterRows = useCallback(_toggleRows(false), [AgGridApiRef]);

    // Function to expand all master rows
    const expandMasterRows = useCallback(_toggleRows(true), [AgGridApiRef]);

    return {
        AgGridApiRef,
        getSelectedRows,
        isMasterRowsExpanded,
        toggleMasterRows,
        collapseMasterRows,
        expandMasterRows,
    };
};
